import { useEffect, useState } from "react";
import api, { fmtINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Truck } from "lucide-react";
import { usePinPrompt } from "@/components/app/PinPrompt";

export default function Vehicles() {
  const pinPrompt = usePinPrompt();
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState(null);
  const [range, setRange] = useState({ frm: "", to: "" });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ number: "", kind: "Truck", driver_name: "", driver_phone: "", freight_per_km: 0 });

  const load = async () => setRows((await api.get("/vehicles")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    await pinPrompt();
    try { await api.post("/vehicles", f); toast.success("Vehicle added"); setOpen(false); setF({ number: "", kind: "Truck", driver_name: "", driver_phone: "", freight_per_km: 0 }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await pinPrompt();
    try { await api.delete(`/vehicles/${id}`); toast.success("Deleted"); load(); if (selected === id) { setSelected(null); setReport(null); } }
    catch (e) { toast.error("Failed"); }
  };

  const openReport = async (id) => {
    setSelected(id);
    const params = new URLSearchParams();
    if (range.frm) params.set("frm", range.frm);
    if (range.to) params.set("to", range.to);
    const { data } = await api.get(`/vehicles/${id}/report?${params}`);
    setReport(data);
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3">
        <div>
          <div className="overline">Fleet</div>
          <h1 className="text-3xl font-bold tracking-tight">Vehicles</h1>
        </div>
        <Button className="btn-primary" onClick={() => setOpen(true)} data-testid="vehicle-add"><Plus size={14} /> Add Vehicle</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-border rounded-lg overflow-hidden">
          <table className="acc-table w-full">
            <thead><tr><th>Number</th><th>Kind</th><th className="num">₹/km</th><th></th></tr></thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v._id_str} className={selected === v._id_str ? "bg-emerald-50" : ""} data-testid={`vehicle-row-${v._id_str}`}>
                  <td><button onClick={() => openReport(v._id_str)} className="font-mono font-semibold text-[#064E3B] underline">{v.number}</button></td>
                  <td>{v.kind}</td>
                  <td className="num">{v.freight_per_km}</td>
                  <td><button className="text-red-600 hover:bg-red-50 p-1 rounded" onClick={() => del(v._id_str)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-muted-foreground">No vehicles.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 bg-white border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Input type="date" value={range.frm} onChange={(e) => setRange({ ...range, frm: e.target.value })} className="w-40" data-testid="vehicle-range-from" />
            <Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-40" data-testid="vehicle-range-to" />
            <Button variant="outline" onClick={() => selected && openReport(selected)} data-testid="vehicle-report-apply">Apply</Button>
          </div>
          {!report && <div className="text-sm text-muted-foreground p-6 text-center">Pick a vehicle from the left to see its trip report, KM, and revenue.</div>}
          {report && (
            <>
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div className="border border-border rounded-md p-3">
                  <div className="overline">Trips</div>
                  <div className="text-2xl font-mono font-bold">{report.trips}</div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="overline">KM Total</div>
                  <div className="text-2xl font-mono font-bold">{report.total_km}</div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="overline">Revenue</div>
                  <div className="text-2xl font-mono font-bold text-[#166534]">{fmtINR(report.total_revenue)}</div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="overline">Estimated Freight</div>
                  <div className="text-2xl font-mono font-bold">{fmtINR(report.estimated_freight)}</div>
                </div>
              </div>
              <table className="acc-table w-full">
                <thead><tr><th>Date</th><th>Number</th><th>Party</th><th className="num">KM</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {report.challans.map((c) => (
                    <tr key={c._id_str}>
                      <td>{new Date(c.date).toLocaleDateString()}</td>
                      <td className="font-mono">{c.number}</td>
                      <td>{c.party_name}</td>
                      <td className="num">{c.km}</td>
                      <td className="num">{fmtINR(c.total)}</td>
                    </tr>
                  ))}
                  {report.challans.length === 0 && <tr><td colSpan={5} className="text-center py-4 text-muted-foreground">No trips.</td></tr>}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add Vehicle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Number*</Label><Input value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} data-testid="vehicle-form-number" /></div>
            <div><Label>Kind</Label><Input value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} /></div>
            <div><Label>Driver</Label><Input value={f.driver_name} onChange={(e) => setF({ ...f, driver_name: e.target.value })} /></div>
            <div><Label>Driver phone</Label><Input value={f.driver_phone} onChange={(e) => setF({ ...f, driver_phone: e.target.value })} /></div>
            <div><Label>Freight per KM (₹)</Label><Input type="number" value={f.freight_per_km} onChange={(e) => setF({ ...f, freight_per_km: +e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-primary" onClick={save} data-testid="vehicle-form-save">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
